import { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Search,
} from "lucide-react";
import {
  format,
  addDays,
  startOfToday,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isAfter,
  isBefore,
  isToday,
} from "date-fns";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { calculateCalendarDuration } from "@/lib/calendar";
import {
  MIN_SEARCH_RENTAL_DAYS,
  getMinRentalEndDate,
  enforceMinSearchRentalRange,
} from "@/lib/calendar/search-min-rental";
import { getDateLocale, getWeekdaysShort } from "@/lib/i18n/formatLocale";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Presets rapides pour les dates
const getDatePresets = (t: (key: string) => string) => [
  { label: t("calendar.presetThisWeekend"), getDates: () => getWeekend() },
  { label: t("calendar.presetNextWeek"), getDates: () => getNextWeek() },
  { label: t("calendar.preset30Days"), getDates: () => getNext30Days() },
];

function getWeekend(): DateRange {
  const today = startOfToday();
  const dayOfWeek = today.getDay();
  const daysUntilSaturday = dayOfWeek === 0 ? 6 : 6 - dayOfWeek;
  const saturday = addDays(today, daysUntilSaturday);
  return { from: saturday, to: getMinRentalEndDate(saturday) };
}

function getNextWeek(): DateRange {
  const today = startOfToday();
  const dayOfWeek = today.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const nextMonday = addDays(today, daysUntilMonday);
  const nextSunday = addDays(nextMonday, 6);
  return { from: nextMonday, to: nextSunday };
}

function getNext30Days(): DateRange {
  const today = startOfToday();
  return { from: today, to: addDays(today, 30) };
}

interface EnhancedDatePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onConfirm: () => void;
  /** Called with the completed range so the parent can close and search without a stale state read. */
  onRangeCommitted?: (range: DateRange) => void;
  showSearch?: boolean;
}

export function EnhancedDatePicker({
  open,
  onOpenChange,
  dateRange,
  onDateRangeChange,
  onConfirm,
  onRangeCommitted,
  showSearch = false,
}: EnhancedDatePickerProps) {
  const { i18n, t } = useTranslation();
  const dateLocale = getDateLocale(i18n.language);
  const weekdays = getWeekdaysShort(i18n.language);
  const datePresets = useMemo(() => getDatePresets(t), [t]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const today = startOfToday();
  const maxDate = addDays(today, 365);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });

    // Add padding for the first week
    let startOffset = start.getDay() - 1;
    if (startOffset < 0) startOffset = 6;

    const paddedDays: (Date | null)[] = Array(startOffset).fill(null);
    return [...paddedDays, ...days];
  }, [currentMonth]);

  // Generate calendar days for next month
  const nextMonthDays = useMemo(() => {
    const nextMonth = addMonths(currentMonth, 1);
    const start = startOfMonth(nextMonth);
    const end = endOfMonth(nextMonth);
    const days = eachDayOfInterval({ start, end });

    let startOffset = start.getDay() - 1;
    if (startOffset < 0) startOffset = 6;

    const paddedDays: (Date | null)[] = Array(startOffset).fill(null);
    return [...paddedDays, ...days];
  }, [currentMonth]);

  const commitRange = (range: DateRange | undefined) => {
    onDateRangeChange(range);
    if (!range?.from || !range.to || !onRangeCommitted) return;
    const duration = calculateCalendarDuration({
      startDate: range.from,
      endDate: range.to,
    });
    if (duration >= MIN_SEARCH_RENTAL_DAYS) {
      onRangeCommitted(range);
    }
  };

  const handleDateClick = (date: Date) => {
    if (isBefore(date, today) || isAfter(date, maxDate)) return;

    if (!dateRange?.from) {
      onDateRangeChange({ from: date, to: undefined });
    } else if (!dateRange.to) {
      if (isBefore(date, dateRange.from)) {
        onDateRangeChange({ from: date, to: undefined });
      } else {
        commitRange(enforceMinSearchRentalRange(dateRange.from, date, maxDate));
      }
    } else {
      onDateRangeChange({ from: date, to: undefined });
    }
  };

  const getPreviewEndDate = (): Date | null => {
    if (!dateRange?.from) return null;
    if (dateRange.to) return dateRange.to;
    if (!hoverDate || !isAfter(hoverDate, dateRange.from)) return null;

    const hoveredDuration = calculateCalendarDuration({
      startDate: dateRange.from,
      endDate: hoverDate,
    });
    if (hoveredDuration < MIN_SEARCH_RENTAL_DAYS) {
      const minEnd = getMinRentalEndDate(dateRange.from);
      return isAfter(minEnd, maxDate) ? maxDate : minEnd;
    }
    return hoverDate;
  };

  const isInRange = (date: Date) => {
    if (!dateRange?.from) return false;

    const endDate = getPreviewEndDate();
    if (!endDate) return false;

    return isAfter(date, dateRange.from) && isBefore(date, endDate);
  };

  const isRangeStart = (date: Date) => dateRange?.from && isSameDay(date, dateRange.from);
  const isRangeEnd = (date: Date) => {
    const end = getPreviewEndDate();
    return end ? isSameDay(date, end) : false;
  };

  const applyPreset = (preset: (typeof datePresets)[number]) => {
    const range = preset.getDates();
    if (!range.from || !range.to) {
      onDateRangeChange(range);
      return;
    }
    commitRange(enforceMinSearchRentalRange(range.from, range.to, maxDate));
  };

  const goToPreviousMonth = () => setCurrentMonth((prev) => subMonths(prev, 1));
  const goToNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));

  const canGoPrevious = isAfter(startOfMonth(currentMonth), today);

  const getDayClassName = (date: Date) => {
    const isPast = isBefore(date, today);
    const isFuture = isAfter(date, maxDate);
    const isDisabled = isPast || isFuture;
    const isStart = isRangeStart(date);
    const isEnd = isRangeEnd(date);
    const inRange = isInRange(date);
    const isTodayDate = isToday(date);

    return cn(
      "relative flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night focus-visible:ring-offset-2 sm:h-10 sm:w-10",

      // Disabled state
      isDisabled && "text-gray-300 cursor-not-allowed",

      // Today
      !isDisabled &&
        isTodayDate &&
        !isStart &&
        !isEnd &&
        "ring-2 ring-primary/50 font-bold text-primary",

      // Start date
      isStart && "z-10 bg-green-night text-white",

      // End date
      isEnd && !isStart && "z-10 bg-green-night text-white",

      // In range
      inRange && !isStart && !isEnd && "bg-primary/10 text-primary",

      // Normal available day
      !isDisabled &&
        !isStart &&
        !isEnd &&
        !inRange &&
        "cursor-pointer text-text-primary hover:bg-scandi-bg"
    );
  };

  const renderMonth = (days: (Date | null)[], monthDate: Date) => (
    <div className="flex-1 min-w-0">
      {/* Titre du mois - caché sur mobile (déjà dans navigation), visible sur desktop */}
      <div className="hidden sm:block text-center mb-3 sm:mb-4">
        <h3 className="font-semibold text-gray-900 capitalize text-sm sm:text-base">
          {format(monthDate, "MMMM yyyy", { locale: dateLocale })}
        </h3>
      </div>

      {/* Weekday headers - optimisé mobile */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
        {weekdays.map((day) => (
          <div
            key={day}
            className="text-center text-[9px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider py-0.5 sm:py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Days grid - optimisé mobile */}
      <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
        {days.map((date, idx) => {
          if (!date) {
            return <div key={`empty-${idx}`} className="h-9 sm:h-10" />;
          }

          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => handleDateClick(date)}
              onMouseEnter={() => dateRange?.from && !dateRange.to && setHoverDate(date)}
              onMouseLeave={() => setHoverDate(null)}
              disabled={isBefore(date, today) || isAfter(date, maxDate)}
              aria-pressed={Boolean(isRangeStart(date) || isRangeEnd(date))}
              aria-label={format(date, "d MMMM yyyy", { locale: dateLocale })}
              className={getDayClassName(date)}
            >
              {format(date, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );

  // 📅 CALENDAR ENGINE: Calculate visual duration (inclusive for UI display)
  const durationDays =
    dateRange?.from && dateRange?.to
      ? calculateCalendarDuration({
          startDate: dateRange.from,
          endDate: dateRange.to,
        })
      : 0;

  const isRangeValid =
    !!dateRange?.from &&
    !!dateRange?.to &&
    durationDays >= MIN_SEARCH_RENTAL_DAYS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[720px] gap-0 overflow-hidden rounded-2xl p-0 max-md:max-h-[92vh] max-md:overflow-y-auto">
        {/* Hidden accessible title */}
        <DialogTitle className="sr-only">{t("calendar.dateSelectionTitle")}</DialogTitle>
        <DialogDescription className="sr-only">{t("calendar.dateSelectionDescription")}</DialogDescription>

        <div className="p-3 sm:p-5">
          {/* Quick presets + Selected dates summary */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 mb-2 sm:mb-4">
            {/* Presets - optimisé mobile */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {datePresets.map((preset) => (
                <Button
                  key={preset.label}
                  variant="outline"
                  size="sm"
                  onClick={() => applyPreset(preset)}
                  className="h-8 shrink-0 rounded-full border-border/70 px-3 text-xs"
                >
                  {preset.label}
                </Button>
              ))}
            </div>

            {/* Selected dates inline - optimisé mobile */}
            <div className="flex items-center gap-1 sm:gap-1.5 ml-0 sm:ml-auto text-[10px] sm:text-sm">
              <span
                className={cn(
                  "font-medium px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md sm:rounded-lg text-[10px] sm:text-xs",
                  dateRange?.from ? "bg-primary/10 text-primary" : "text-gray-400"
                )}
              >
                {dateRange?.from ? format(dateRange.from, "d MMM", { locale: dateLocale }) : t("common.start")}
              </span>
              <span className="text-gray-400 text-[10px] sm:text-xs">→</span>
              <span
                className={cn(
                  "font-medium px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md sm:rounded-lg text-[10px] sm:text-xs",
                  dateRange?.to ? "bg-primary/10 text-primary" : "text-gray-400"
                )}
              >
                {dateRange?.to ? format(dateRange.to, "d MMM", { locale: dateLocale }) : t("common.end")}
              </span>
            </div>
          </div>

          {/* Durée minimum - fallback si la clé i18n n'est pas résolue */}
          <p className="mb-3 text-xs text-text-secondary">
            {(() => {
              const hint = t("search.minRentalHint");
              return hint && hint !== "search.minRentalHint" ? hint : "Durée min. : 3 jours";
            })()}
          </p>

          {/* Calendar navigation */}
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPreviousMonth}
              disabled={!canGoPrevious}
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl border-gray-200 hover:bg-gray-100 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            {/* Mois affiché au centre - visible sur mobile, caché sur desktop */}
            <div className="flex items-center gap-1.5 sm:hidden">
              <CalendarIcon className="h-4 w-4 text-text-tertiary" />
              <span className="text-xs font-semibold text-gray-900 capitalize">
                {format(currentMonth, "MMMM yyyy", { locale: dateLocale })}
              </span>
            </div>
            
            {/* Espaceur pour desktop */}
            <div className="hidden sm:block" />
            
            <Button
              variant="outline"
              size="icon"
              onClick={goToNextMonth}
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg sm:rounded-xl border-gray-200 hover:bg-gray-100"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendars — un mois plein écran sur mobile ; deux mois côte à côte sur desktop */}
          <div role="region" aria-label={t("calendar.dateSelectionTitle")}>
            <div className="flex w-full flex-col gap-6 pb-1 sm:flex-row sm:gap-4 sm:w-full sm:min-w-full">
              <div className="w-full min-w-0 sm:flex-1">
                {renderMonth(calendarDays, currentMonth)}
              </div>
              <div className="hidden sm:block flex-shrink-0 w-px bg-gray-100 self-stretch" aria-hidden />
              <div className="hidden w-full min-w-0 sm:block sm:flex-1 sm:border-l sm:border-gray-100 sm:pl-4">
                {renderMonth(nextMonthDays, addMonths(currentMonth, 1))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - optimisé mobile */}
        <DialogFooter className="flex flex-row items-center gap-2 border-t border-border/70 bg-card px-4 py-3 sm:px-5">
          {durationDays > 0 && (
            <p className="mr-auto text-sm font-medium text-text-primary">
              {durationDays} {durationDays > 1 ? t("common.days") : t("common.day")}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-10 rounded-full px-4 text-sm"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={!isRangeValid}
            className="h-10 rounded-full bg-green-night px-5 text-sm font-semibold text-white hover:bg-green-night/90"
          >
            {showSearch ? (
              <>
                <Search className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {t("nav.search")}
              </>
            ) : (
              t("common.confirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EnhancedDatePicker;
